import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import type { ErpConnectionView, ErpMatchApplyResult, ErpMatchReview, ErpMatchRow } from '../../../api';
import { erpApi } from '../../../api';
import { useIdempotencyKey } from '../../../idempotency';
import { Dialog, FilterChips, Input, Pager, Table } from '../../primitives';
import { ErrorNotice, LoadingRows, StatusBadge } from '../../ui';
import type { LinkedWebsite } from '../labels';
import { formatNumber, websiteLabel } from '../labels';
import { BatchResult } from './BatchResult';
import type { LeftOutRow } from './BatchResult';
import { countOf, matchEvidenceLegend, nameCheckText, noteText, problemTitle } from './matchingCopy';

type Show = 'all' | 'agree' | 'check' | 'blocked';

/** A review can have thousands of pairs: they are shown a page at a time, though ticking works across all of them. */
const PAGE = 100;

/** A pair can be ticked when it is ready to send and the engine offers it: names agree, or an identical identifier. */
const tickable = (row: ErpMatchRow) => row.status === 'ready' && (row.bulk === 'selected' || row.bulk === 'optional');

/**
 * Reviewing the suggested pairs of a website and saving the ones a person
 * ticks (CO-170). A pair is listed when the product's SKU equals an article's
 * code or barcode; each row says exactly that, and whether the two names
 * agree. Pairs whose names agree are ticked, an identical identifier with
 * names that are not known to agree is offered unticked, and a code that is
 * equal only apart from leading zeros while the names disagree cannot be
 * ticked at all: those are listed so the conflict is seen, with a way to look
 * at the product on its own. The server checks every pair again, so what is
 * not offered here cannot be sent by other means.
 */
export function ReviewMatchesDialog({
  open,
  connection,
  target,
  withStore,
  onClose,
  onDone,
  onReviewProduct,
}: {
  open: boolean;
  connection: ErpConnectionView;
  target: LinkedWebsite;
  withStore: boolean;
  onClose: () => void;
  /** The store was asked something: the list is read again. */
  onDone: () => void;
  /** Look at one product on its own, in the workspace. */
  onReviewProduct: (sku: string) => void;
}) {
  const [review, setReview] = useState<ErpMatchReview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [show, setShow] = useState<Show>('all');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ErpMatchApplyResult | null>(null);
  const [leftOut, setLeftOut] = useState<LeftOutRow[]>([]);
  const idempotency = useIdempotencyKey();
  const allId = useId();
  const website = websiteLabel(target.store, target.website, withStore);

  const load = useCallback(async () => {
    setError(null);
    try {
      const next = await erpApi.reviewMatches(connection.id, { installation_id: target.store.id, website_code: target.website.code });
      setReview(next);
      // The pairs the evidence supports are ticked; the rest wait for a person.
      setTicked(new Set(next.rows.filter((row) => tickable(row) && row.bulk === 'selected').map((row) => row.sku)));
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }, [connection.id, target.store.id, target.website.code]);

  useEffect(() => {
    if (!open) return;
    setReview(null);
    setResult(null);
    setLeftOut([]);
    setShow('all');
    setPage(0);
    void load();
  }, [open, load]);

  const rows = review?.rows ?? [];
  const groups = useMemo(
    () => ({
      agree: rows.filter((row) => row.bulk === 'selected' && row.status === 'ready'),
      check: rows.filter((row) => row.bulk === 'optional' && row.status === 'ready'),
      blocked: rows.filter((row) => !tickable(row)),
    }),
    [rows],
  );
  const shown = show === 'all' ? rows : groups[show];
  const pageRows = shown.slice(page * PAGE, page * PAGE + PAGE);
  const selectable = rows.filter(tickable);
  const tickedCount = selectable.filter((row) => ticked.has(row.sku)).length;
  const allTicked = selectable.length > 0 && tickedCount === selectable.length;

  function toggle(sku: string, on: boolean) {
    setTicked((current) => {
      const next = new Set(current);
      if (on) next.add(sku);
      else next.delete(sku);
      return next;
    });
  }
  function toggleAll(on: boolean) {
    setTicked(on ? new Set(selectable.map((row) => row.sku)) : new Set());
  }

  async function save() {
    if (!review || tickedCount === 0) return;
    setBusy(true);
    setError(null);
    const chosen = selectable.filter((row) => ticked.has(row.sku));
    const key = idempotency.key();
    try {
      const done = await erpApi.applyMatches(
        connection.id,
        {
          installation_id: target.store.id,
          website_code: target.website.code,
          source: 'review',
          pairs: chosen.map((row) => ({ sku: row.sku, article_code: row.article_code })),
        },
        key,
      );
      idempotency.settle();
      setLeftOut(
        rows
          .filter((row) => !ticked.has(row.sku) || !tickable(row))
          .map((row) => ({ sku: row.sku, articleCode: row.article_code, reason: tickable(row) ? 'Not ticked' : row.message ?? problemTitle(row.reason) })),
      );
      setResult(done);
      onDone();
    } catch (cause) {
      idempotency.settle(cause);
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  const footer = result ? (
    <button type="button" className="button" onClick={onClose}>
      Done
    </button>
  ) : (
    <>
      <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button type="button" className="button" onClick={() => void save()} disabled={busy || tickedCount === 0} data-testid="review-save">
        {busy ? 'Asking the store' : `Save ${countOf(tickedCount, 'match', 'matches')}`}
      </button>
    </>
  );

  return (
    <Dialog
      open={open}
      size="lg"
      title="Review suggested matches"
      description={`Products in ${website} whose SKU is an ERP article’s code or barcode.`}
      onClose={onClose}
      busy={busy}
      footer={footer}
      testId="review-matches-dialog"
      className="dialog--matches"
    >
      {result ? (
        <BatchResult connectionId={connection.id} result={result} leftOut={leftOut} onChanged={onDone} />
      ) : (
        <>
          <ErrorNotice error={error} />
          {!review && !error ? (
            <LoadingRows label="Finding the suggested pairs" rows={5} rowHeight="44px" />
          ) : review && rows.length === 0 ? (
            <p className="quiet-empty" data-testid="review-empty">
              No product here has a SKU that is an ERP article’s code or barcode.
            </p>
          ) : review ? (
            <>
              <p className="match-plan__summary" data-testid="review-summary">
                {matchEvidenceLegend}
              </p>
              <FilterChips
                label="Show pairs"
                value={show}
                onChange={(next) => {
                  setShow(next);
                  setPage(0);
                }}
                options={[
                  { value: 'all' as Show, label: 'All pairs', count: rows.length },
                  { value: 'agree' as Show, label: 'Names agree', count: groups.agree.length },
                  { value: 'check' as Show, label: 'Check first', count: groups.check.length },
                  { value: 'blocked' as Show, label: 'Not offered', count: groups.blocked.length },
                ]}
              />
              <Table label="Suggested pairs" compact keepRoles className="match-plan match-plan--review" wrapperClassName="match-plan__scroll">
                <caption className="visually-hidden">Suggested pairs: tick the ones to save</caption>
                <thead role="rowgroup">
                  <tr role="row">
                    <th scope="col" className="match-plan__check" role="columnheader">
                      <Input
                        id={allId}
                        type="checkbox"
                        checked={allTicked}
                        disabled={selectable.length === 0}
                        onChange={(event) => toggleAll(event.target.checked)}
                        aria-label="Tick every pair that can be ticked, on every page"
                      />
                    </th>
                    <th scope="col" role="columnheader">
                      Store product
                    </th>
                    <th scope="col" role="columnheader">
                      ERP article
                    </th>
                    <th scope="col" role="columnheader">
                      Why it is suggested
                    </th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {pageRows.map((row) => {
                    const can = tickable(row);
                    return (
                      <tr key={row.sku} role="row" className={`${can ? '' : 'match-plan__row--off'}${ticked.has(row.sku) && can ? ' match-plan__row--on' : ''}`} data-testid={`review-row-${row.sku}`}>
                        <td className="match-plan__check" role="cell">
                          <Input
                            type="checkbox"
                            checked={can && ticked.has(row.sku)}
                            disabled={!can}
                            onChange={(event) => toggle(row.sku, event.target.checked)}
                            aria-label={`Save ${row.product_name || row.sku} as ${row.article_name || row.article_code}`}
                          />
                        </td>
                        <td role="cell" data-label="Store product">
                          <span className="match-plan__primary">{row.product_name || row.sku}</span>
                          <code className="match-plan__secondary">{row.sku}</code>
                        </td>
                        <td role="cell" data-label="ERP article">
                          <span className="match-plan__primary">{row.article_name || row.article_code}</span>
                          <code className="match-plan__secondary">
                            {row.article_code}
                            {row.article_barcode ? ` · ${row.article_barcode}` : ''}
                          </code>
                        </td>
                        <td role="cell" data-label="Why it is suggested">
                          <span className="match-plan__evidence">
                            {(row.why ?? []).map((reason) => (
                              <StatusBadge key={reason} kind="matchEvidence" value={reason} />
                            ))}
                            {row.name_check ? <StatusBadge kind="matchNames" value={row.name_check} /> : null}
                          </span>
                          {row.status === 'error' ? (
                            <span className="match-plan__detail">{row.message ?? problemTitle(row.reason)}</span>
                          ) : row.bulk === 'blocked' ? (
                            <span className="match-plan__detail">
                              {row.ambiguous ? 'More than one article has this code.' : nameCheckText.differ} Not offered for a batch.{' '}
                              <button type="button" className="button button--ghost button--small" onClick={() => {
                                onClose();
                                onReviewProduct(row.sku);
                              }}>
                                Review {row.sku} on its own
                              </button>
                            </span>
                          ) : row.status === 'unchanged' ? (
                            <span className="match-plan__detail">Already matched, or the store was already asked.</span>
                          ) : row.bulk === 'optional' ? (
                            <span className="match-plan__detail">The identifiers are equal, but the names are not known to agree. Read both before you tick it.</span>
                          ) : null}
                          {noteText(row.notes).map((text) => (
                            <span key={text} className="match-plan__detail match-plan__detail--note">
                              {text}
                            </span>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {shown.length > PAGE ? (
                <Pager
                  first={page * PAGE + 1}
                  last={Math.min(page * PAGE + PAGE, shown.length)}
                  total={shown.length}
                  onPrevious={() => setPage(page - 1)}
                  onNext={() => setPage(page + 1)}
                />
              ) : null}
              {review.truncated ? <p className="quiet-empty">The first {formatNumber(rows.length)} pairs are listed.</p> : null}
              {review.other_products > 0 ? (
                <p className="quiet-empty" data-testid="review-others">
                  {formatNumber(review.other_products)} other {review.other_products === 1 ? 'product has' : 'products have'} no code or barcode that equals an article’s.
                </p>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
